import React from 'react';
import { TFunction } from 'i18next';
import {
  Chip,
  DataTableMeta,
  DateColumn,
  LimitedContent,
  Typography,
} from '@otp/ui';
import { WttpQuestion } from 'src/graphql/generated/graphql';
import { getFullName } from 'src/utils/user-data';
import { ActionsColumn } from './columns/actions';
import { WttpQuestionAction } from 'src/components/wttp/questions/list/table/columns/actions/actions-modals';
import { StatusColumn } from 'src/components/wttp/common/table/columns/status';
import { PartialUser } from 'src/types';

type ImportanceValue = 'Low' | 'Medium' | 'High';

const colorMapping = {
  Low: 'default',
  Medium: 'info',
  High: 'error',
};

export type WttpQuestionsData = Omit<WttpQuestion, 'createdByUser'> & {
  createdByUser?: PartialUser;
};

export const getTableConfig = (
  questions: WttpQuestionsData[],
  {
    t,
    onActionFinished,
  }: { t: TFunction; onActionFinished?: (action: WttpQuestionAction) => void },
) => [
  {
    name: 'isActive',
    label: t('common.status.label'),
    options: {
      customBodyRender: (isActive: boolean, column: DataTableMeta) => {
        const question = questions[column.rowIndex];

        return <StatusColumn status={question.status} isActive={isActive} />;
      },
    },
  },
  {
    name: 'title',
    label: t('wttp.manage-questions.table.question'),
    options: {
      customBodyRender: (value: any, column: DataTableMeta) => {
        const rowData = questions[column.rowIndex];
        return (
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            className="whitespace-normal"
          >
            {rowData.title}
          </Typography>
        );
      },
    },
  },
  {
    name: 'description',
    label: t('wttp.manage-questions.table.description'),
    options: {
      customBodyRender: (value: string) => {
        return (
          <LimitedContent maxRows={2}>
            <div dangerouslySetInnerHTML={{ __html: value }} />
          </LimitedContent>
        );
      },
    },
  },
  {
    name: 'updatedAt',
    label: t('common.updated-at'),
    options: {
      customBodyRender: (value: string, column: DataTableMeta) => {
        const rowData = questions[column.rowIndex];

        return (
          <Typography variant="subtitle1">
            <DateColumn date={value || rowData.createdAt} />
          </Typography>
        );
      },
    },
  },
  {
    name: 'createdByUser',
    label: t('wttp.manage-questions.table.created-by'),
    options: {
      customBodyRender: (value: any, column: DataTableMeta) => {
        const rowData = questions[column.rowIndex];
        if (!rowData.createdByUser) {
          return null;
        }

        return (
          <Typography variant="subtitle1">
            {getFullName(rowData.createdByUser)}
          </Typography>
        );
      },
    },
  },
  {
    name: 'category',
    label: t('wttp.manage-questions.table.category'),
    options: {
      customBodyRender: (value: any, column: DataTableMeta) => {
        const rowData = questions[column.rowIndex];
        return (
          <Chip label={rowData.category?.name} color="primary" size="small" />
        );
      },
    },
  },
  {
    name: 'importance',
    label: t('wttp.manage-questions.table.importance'),
    options: {
      customBodyRender: (value: ImportanceValue) => {
        const mappedColor = colorMapping[value] as any;
        return <Chip label={value} color={mappedColor} size="small" />;
      },
    },
  },
  {
    name: 'actions',
    label: t('common.actions'),
    options: {
      customBodyRender: (_: any, column: DataTableMeta) => {
        const question = questions[column.rowIndex];

        return (
          <ActionsColumn
            wttpQuestion={question}
            onActionFinished={onActionFinished}
          />
        );
      },
    },
  },
];
